import {Component, HostBinding, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ConfirmPopupComponent } from 'src/app/components';
import { ReportPageConstant } from 'src/app/constants/reportPage.constants';
import { ReportPageInterface } from 'src/app/interface';
import { Report, Location, LocationType, Zip, FieldValue, FieldValueReset } from 'src/app/model';
import { FooterButtonOption, PageNumbers } from 'src/app/model/Enums';
import { CommonProvider, ReportProvider } from 'src/app/providers';
import {CommonService, ReportService, ToasterService} from 'src/app/services';
import { radioGroupOption } from '../../commonModel/common-classes';
import {routeTransition} from '../../../../animations/route-transition';

@Component({
  selector: 'app-location',
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss'],
  animations: [routeTransition],
})
export class LocationComponent implements OnInit, OnDestroy, ReportPageInterface {
  @HostBinding('@routeTransition') routeAnimation = true;
  public currentReport: Report;
  private reportSub: Subscription;
  reportPageConst = ReportPageConstant;

  //#region "location-amount"
  locationSize: number = this.common.isTabletDevice || this.common.isMobileDevice ? 3 : 10;
  selectedLocationLength: number = 0;
  isEnterMore: boolean = false;
  amountList: radioGroupOption[] = [];
  //#endregion

  //#region "location-details"
  @ViewChild('locationForm') locationForm: NgForm;
  selectedLocation = new Location();
  locTypeList: radioGroupOption[] = [];
  booleanOptions: radioGroupOption[] = [];
  locationType = LocationType;
  zipModelChanged: Subject<string> = new Subject<string>();
  shadowLocationName: string;
  //#endregion

  constructor(
    public reportService: ReportService,
    private reportProvider: ReportProvider,
    private modalService: BsModalService,
    private router: Router,
    private toastr: ToasterService,
    private commonProvider: CommonProvider,
    private common: CommonService,
  ) {
    this.zipModelChanged.pipe(
      debounceTime(500))
      .subscribe(model => {
        this.zipEntered();
      });
  }

  ngOnInit(): void {
    for (let i = 1; i <= this.locationSize; i++) {
      const optObject = new radioGroupOption();
      optObject.title = i.toString();
      optObject.value = i;
      this.amountList.push(optObject);
    }

    this.locTypeList.push({ title: 'Retail (Restaurant, Gift Shop, etc.)', value: LocationType.RETAIL });
    this.locTypeList.push({ title: 'Industrial (Warehouse, Manufacturing Plant, etc.)', value: LocationType.INDUSTRIAL });
    this.locTypeList.push({ title: 'Office (Professional Services, Healthcare, etc.)', value: LocationType.OFFICE });
    this.locTypeList.push({ title: 'Other', value: LocationType.OTHER });

    this.booleanOptions.push({ title: 'Yes', value: true });
    this.booleanOptions.push({ title: 'No', value: false });

    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;
      if (this.currentReport && this.currentReport.LATEST) {
        if (this.currentReport.LATEST.LOCATION_LIST.length) {
          this.currentReport.LATEST.LOCATION_LIST.sort((a, b) => Number(a.ORDER) - Number(b.ORDER));
          this.selectLocation(this.currentReport.LATEST.LOCATION_LIST[0]);
        } else {
          if (!this.selectedLocation.ID)
            this.checkSelectedLocation();
        }
      }
    });
  }

  //#region "location-amount"
  /**
   * @description for create selected no. of locations
   */
  locationSelection() {
    if (!this.currentReport.LATEST.LOCATION_LIST.length) {
      if (this.selectedLocationLength) {
        this.toastr.info('We are preparing locations, please wait a moment.');
        this.reportProvider.addDefaultLocation(this.currentReport.LATEST.ID, this.selectedLocationLength).subscribe(resp => {
          if (resp.success) {
            this.currentReport.LATEST.LOCATION_LIST = resp.result;
            this.currentReport.LATEST.LOCATION_LIST.sort((a, b) => Number(a.ORDER) - Number(b.ORDER));
            this.currentReport.LATEST.LOCATION_LIST.forEach(element => {
              element.ZIP = new Zip();
            });

            if (this.currentReport.LATEST.LOCATION_LIST.length) {
              this.selectLocation(this.currentReport.LATEST.LOCATION_LIST[0]);
            }

          } else {
            this.toastr.error(resp.error.txt);
          }
        }, err => {
          // Error Handling
        });
      } else {
        if (this.reportSub)
          this.reportSub.unsubscribe();
      }
    }
  }
  //#endregion

  //#region "location-details"
  checkSelectedLocation() {
    if (!this.selectedLocation.ID) {
      setTimeout(() => {
        if (this.currentReport.LATEST.LOCATION_LIST.length) {
          this.selectLocation(this.currentReport.LATEST.LOCATION_LIST[0]);
        } else {
          this.checkSelectedLocation();
        }
      }, 500);
    }
  }

  ngOnDestroy(): void {
    if (this.reportSub)
      this.reportSub.unsubscribe();
  }

  /**
   * @description create/add new location
   */
  addBlankLocation() {
    let newLocationName = '';
    const regexp = new RegExp('Location\\s(\\d+)\\b');
    const numberArray = this.currentReport.LATEST.LOCATION_LIST.filter(({ NAME }) => regexp.test(NAME)).map(el => Number(el.NAME.match(/\d+/g)));
    if (numberArray.length) {
      newLocationName = 'Location ' + Number(Math.max(...numberArray) + 1);
    } else {
      newLocationName = 'Location 2';
    }

    this.toastr.info('Please wait a moment!');
    let location = new Location();
    location.NAME = newLocationName;
    location.IS_PRIMARY = this.currentReport.LATEST.LOCATION_LIST.length ? false : true;
    this.selectedLocation = location;
    this.reportProvider.saveLocation(this.currentReport.LATEST.ID, this.selectedLocation).subscribe(resp => {
      if (resp.success) {
        this.selectedLocation = resp.result;
        this.selectedLocation.ZIP = new Zip();
        this.currentReport.LATEST.LOCATION_LIST.push(resp.result);
      } else {
        this.toastr.error(resp.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  focusLocationName(): void {
    if (!this.selectedLocation.DISPLAY_NAME){
      this.shadowLocationName = this.selectedLocation.NAME;
      this.selectedLocation.NAME = "";
    }
  }

  makeDefaultName(): void {
    if (this.shadowLocationName){
      this.selectedLocation.NAME = this.shadowLocationName;
    } else this.shadowLocationName = null;

    if (this.selectedLocation.DISPLAY_NAME.trim() == this.selectedLocation.NAME.trim()) {
      this.selectedLocation.DISPLAY_NAME = '';
    }

    setTimeout(() => {
      this.callUpdateAPI('DISPLAY_NAME');
    }, 10);
  }

  /**
   * @description for select location
   * @param location selected location
   */
  selectLocation(location) {
    if (!location.ZIP) {
      location.ZIP = new Zip();
    }
    this.selectedLocation = location;
  }

  /**
   * @description check all added location required fields are valid
   * @returns all added location valid or not
   */
  locationNextDisabled() {
    let flag = true;
    if (this.currentReport && this.currentReport.LATEST) {

      if (!this.currentReport.LATEST.LOCATION_LIST.length)
        return false;

      this.currentReport.LATEST.LOCATION_LIST.forEach(el => {
        if (flag) {
          if (el.LOCATION_TYPE) {

            if (el.LOCATION_TYPE == this.locationType.OTHER && !el.OTHER_TYPE) {
              flag = false;
            }

            if (!el.NAME) {
              flag = false;
            }

            if (!el.ZIP) {
              flag = false;
            } else if (el.ZIP && !el.ZIP.ZIP) {
              flag = false;
            }

            if (el.IS_OWNED === true) {
              if (el.IS_OWNED_ON_BS !== true && el.IS_OWNED_ON_BS !== false) {
                flag = false;
              }
            } else if (el.IS_OWNED === false) {
              if (el.IS_RENT_INCREASE !== true && el.IS_RENT_INCREASE !== false) {
                flag = false;
              }
            } else {
              flag = false;
            }
          } else {
            flag = false;
          }
        }
      });
    }
    return flag;
  }

  /**
   * @description for delete location
   * @param locationId primary key for delete location
   * @param index index for location
   */
  deleteLocation(locationId, index) {

    let confirmModalRef: BsModalRef;
    confirmModalRef = this.modalService.show(ConfirmPopupComponent, Object.assign({
      initialState: {
        message: 'Do you want to delete location?'
      }
    }, { class: 'modal-sm modal-dialog-centered' }));

    const ackSubject = new Subject<boolean>();
    confirmModalRef.content.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value) {
        this.reportProvider.deleteLocation(this.currentReport.LATEST.ID, locationId).subscribe(response => {
          if (response.success) {
            this.currentReport.LATEST.LOCATION_LIST.splice(index, 1);
            this.toastr.success('Location deleted successfully!');
            if (index === 0) {
              this.selectedLocation = this.currentReport.LATEST.LOCATION_LIST[0];
            } else {
              this.selectedLocation = this.currentReport.LATEST.LOCATION_LIST[index - 1];
            }
          }
        }, err => {
          // Error Handling
        });
      }
    });
  }

  /**
   * @description field update API
   * @param fieldName field name which we want to update
   * @param isZip for zip details update
   */
  callUpdateAPI(fieldName: string, isZip: boolean = false) {

    setTimeout(() => {
      if (this.selectedLocation[fieldName] != null && this.selectedLocation[fieldName] != '') {
        this.autoScroll(fieldName);
      }
    }, 10);

    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'LOCATION';
    objFieldValue.FIELD = fieldName;
    objFieldValue.VALUE = !isZip ? this.selectedLocation[fieldName] : this.selectedLocation.ZIP.ZIP;
    this.reportProvider.updateReportField(this.selectedLocation.ID, objFieldValue).subscribe(res => {
      if (res.success) {
        // for update rent objects
        if (this.currentReport.LATEST.MAX_STAGE >= PageNumbers.RENT_DETAILS &&
          (fieldName == 'LOCATION_TYPE' || fieldName == 'IS_OWNED' ||
            fieldName == 'IS_OWNED_ON_BS' || fieldName == 'IS_RENT_INCREASE' || fieldName == 'ZIP')) {
          this.updateRentFields();
        }

        if (fieldName == 'LOCATION_TYPE' && this.selectedLocation.LOCATION_TYPE != this.locationType.OTHER && this.selectedLocation.OTHER_TYPE) {
          this.selectedLocation.OTHER_TYPE = '';
          this.resetFields(['OTHER_TYPE']);
        } else if (fieldName == 'IS_OWNED' && (this.selectedLocation.IS_OWNED_ON_BS != null || this.selectedLocation.IS_RENT_INCREASE != null)) {
          this.selectedLocation.IS_OWNED_ON_BS = null;
          this.selectedLocation.IS_RENT_INCREASE = null;
          this.resetFields(['IS_OWNED_ON_BS', 'IS_RENT_INCREASE']);
        }
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  autoScroll(fieldName: string) {
    switch (fieldName) {
      case 'LOCATION_TYPE':
        setTimeout(() => {
          if (this.selectedLocation.LOCATION_TYPE == LocationType.OTHER) {
            this.reportService.makeElementFocus('OTHER_TYPE');
          } else {
            this.reportService.makeElementFocus('ADDRESS');
          }
        }, 5);
        break;
      case 'OTHER_TYPE':
        this.reportService.makeElementFocus('ADDRESS');
        break;
      /*case 'NAME':
        this.reportService.makeElementFocus('ADDRESS');
        break;*/
      case 'ADDRESS':
        if (!document?.activeElement?.id || document?.activeElement?.id !== 'OTHER_TYPE') {
          this.reportService.makeElementFocus('ZIP');
        }
        break;
      case 'ZIP':
        this.reportService.makeElementFocus('location-is-owned-type-radio-group-yes');
        break;
      case 'IS_OWNED':
        setTimeout(() => {
          if (this.selectedLocation.IS_OWNED && this.selectedLocation.IS_OWNED != null) {
            this.reportService.makeElementFocus('selected-location-owned-type-radio-group-yes');
          } else if (!this.selectedLocation.IS_OWNED && this.selectedLocation.IS_OWNED != null) {
            this.reportService.makeElementFocus('selected-location-rent-increase-type-radio-group-yes');
          }
        }, 5);
        break;
    }
  }

  updateRentFields() {
    if (this.selectedLocation.MARKET_RENT) {
      let objFieldValue = new FieldValueReset();
      objFieldValue.GROUP = 'LOCATION';
      objFieldValue.FIELD_LIST = [
        'MARKET_RENT',
        'RENT_EXPENSE',
        'RENT_EXPENSE_EACH_YEAR',
        // 'RENT_ADJUSTMENT_YEARLY',
        'AUTO_CALCULATED',
        'RENT_CALCULATION_TYPE',
        'SQUARE_FOOTAGE',
        'FAIR_MARKET_SQFT',
        'APPROXIMATE_VALUE',
        'FULL_OCCUPATION',
        'PERCENT_OCCUPATION',
        'NEGOTIATED_RENT'
      ];
      this.reportProvider.resetReportField(this.selectedLocation.ID, objFieldValue).subscribe(res => {
        if (res.success) {
          this.selectedLocation.MARKET_RENT = null;
          this.selectedLocation.RENT_EXPENSE = null;
          this.selectedLocation.RENT_EXPENSE_EACH_YEAR = null;
          this.selectedLocation.AUTO_CALCULATED = null;
          this.selectedLocation.RENT_CALCULATION_TYPE = null;
          this.selectedLocation.SQUARE_FOOTAGE = null;
          this.selectedLocation.FAIR_MARKET_SQFT = null;
          this.selectedLocation.APPROXIMATE_VALUE = null;
          this.selectedLocation.FULL_OCCUPATION = null;
          this.selectedLocation.PERCENT_OCCUPATION = null;
          this.selectedLocation.NEGOTIATED_RENT = null;
          this.reportService.updateMaxStage(PageNumbers.RENT_DETAILS, true);
          this.reportService.addInvalidStage(PageNumbers.RENT_DETAILS, false, false);
        } else {
          this.toastr.error(res.error.txt);
        }
      }, err => {
        // Error Handling
      });
    } else {
      this.reportService.updateMaxStage(PageNumbers.RENT_DETAILS, true);
      this.reportService.addInvalidStage(PageNumbers.RENT_DETAILS, false, false);
    }
  }

  resetFields(fieldNames: string[] = []) {

    let objFieldValue = new FieldValueReset();
    objFieldValue.GROUP = 'LOCATION';
    objFieldValue.FIELD_LIST = fieldNames;
    this.reportProvider.resetReportField(this.selectedLocation.ID, objFieldValue).subscribe(res => {
      if (!res.success) {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description enter char is valid or not
   * @param event key event
   */
  _keyPress(event: any) {
    const pattern = /[0-9]/;
    let inputChar = String.fromCharCode(event.charCode);

    if (!pattern.test(inputChar))
      event.preventDefault();
  }

  zipModelchangedFun(text: string) {
    this.zipModelChanged.next(text);
  }

  /**
   * @description for insert zip
   */
  zipEntered(event: any = null) {
    if (this.selectedLocation.ZIP.ZIP) {
      if (isNaN(Number(this.selectedLocation.ZIP.ZIP))) {
        this.selectedLocation.ZIP.zipError = "Invalid Zip Code";
        this.selectedLocation.ZIP.ZIP = null;
        return;
      } else {
        if (this.selectedLocation.ZIP.ZIP.length < 5) {
          this.selectedLocation.ZIP.zipError = null;
        }
        if (this.selectedLocation.ZIP.ZIP.length === 5) {
          this.selectedLocation.ZIP.zipError = null;
          this.commonProvider.identifyZip(this.selectedLocation.ZIP.ZIP).subscribe(resp => {
            if (resp.success) {
              if (resp.result) {
                this.selectedLocation.ZIP = resp.result;
                this.selectedLocation.STATE = this.selectedLocation.ZIP.STATE;
                this.selectedLocation.CITY = this.selectedLocation.ZIP.CITY;
                this.callUpdateAPI('ZIP', true);
              } else {
                this.selectedLocation.ZIP.zipError = "Invalid Zip Code";
                this.selectedLocation.ZIP = new Zip();
              }
            } else {
              this.selectedLocation.ZIP = new Zip();
              this.toastr.error('Invalid Zip Code!');
            }
          }, err => {
            // Error Handling
          });
        }
      }
    } else {
      this.selectedLocation.ZIP.zipError = null;
    }
  }

  focusOutZip() {
    if (this.selectedLocation.ZIP.ZIP) {
      if (isNaN(Number(this.selectedLocation.ZIP.ZIP))) {
        this.selectedLocation.ZIP.zipError = "Invalid Zip Code";
        this.selectedLocation.ZIP.ZIP = null;
        return;
      }
    }
  }
  //#endregion

  /**
   * @description function call at the time of footer button click, for switch route or location
   */
  switchNextLocation() {
    const currentLocationIndex = this.currentReport.LATEST.LOCATION_LIST.findIndex(loc => loc.ID == this.selectedLocation.ID);
    if (currentLocationIndex === this.currentReport.LATEST.LOCATION_LIST.length - 1) {
      if (this.locationNextDisabled()) {
        this.reportService.updateMaxStage(PageNumbers.LOCATION);
        this.router.navigateByUrl(`/report/${this.currentReport.ID}/ownership`);
      } else {
        this.toastr.error("Please fill all required fields of all added locations !")
      }
    } else if (currentLocationIndex < this.currentReport.LATEST.LOCATION_LIST.length - 1) {
      this.selectLocation(this.currentReport.LATEST.LOCATION_LIST[currentLocationIndex + 1]);
    }
  }

  /**
   * @description function call at the time of footer button click, for switch route or location
   */
  switchBackLocation() {
    const currentLocationIndex = this.currentReport.LATEST.LOCATION_LIST.findIndex(loc => loc.ID == this.selectedLocation.ID);
    if (currentLocationIndex <= 0) {
      this.router.navigateByUrl(`/report/${this.currentReport.ID}/overview`);
    } else if (currentLocationIndex > 0) {
      this.selectLocation(this.currentReport.LATEST.LOCATION_LIST[currentLocationIndex - 1]);
    }
  }

  /**
   * @description function of interface for validation
   * @returns form valid or not
   */
  isFormValid(): boolean {
    return this.locationNextDisabled();
  }

  /**
   * @description function of interface for skip functionality
   * @returns form can be skippable or not
   */
  canBeSkipped(): boolean {
    return false;
  }

  /**
   * @description report footer button click options
   * @param buttonOption which button clicked by user
   */
  footerButtonClick(buttonOption: number) {
    if (buttonOption == FooterButtonOption.NEXT_CLICK) {
      if (this.reportService.showLocationAmountPage()) {
        if (this.selectedLocationLength) {
          this.locationSelection();
        } else {
          this.router.navigateByUrl(`/report/${this.currentReport.ID}/ownership`);
        }
      } else {
        if (document.getElementById('reportPart')) {
          document.getElementById('reportPart').scrollTo(0, 0);
        }
        this.switchNextLocation();
      }
    } else if (buttonOption == FooterButtonOption.BACK_CLICK) {
      if (document.getElementById('reportPart')) {
        document.getElementById('reportPart').scrollTo(0, 0);
      }
      this.switchBackLocation();
    } else if (buttonOption == FooterButtonOption.SKIP_CLICK) {
      this.reportService.updateMaxStage(PageNumbers.LOCATION);
      this.router.navigateByUrl(`/report/${this.currentReport.ID}/ownership`);
    }
  }

}
